import { LitElement, html, css } from 'lit';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { reversePainterSortStable } from '@pmndrs/uikit';
import { forwardHtmlEvents } from '@pmndrs/pointer-events';
import { ReadyMixin } from './base.js';

export class ThreeScene extends ReadyMixin(LitElement) {
  static properties = {
    background: { type: String },
    fogColor: { type: String, attribute: 'fog-color' },
    fogNear: { type: Number, attribute: 'fog-near' },
    fogFar: { type: Number, attribute: 'fog-far' },
    fps: { type: Number },
    // opt-in UnrealBloom postprocessing (per-scene; direct render otherwise)
    bloom: { type: Boolean },
    bloomStrength: { type: Number, attribute: 'bloom-strength' },
    bloomRadius: { type: Number, attribute: 'bloom-radius' },
    bloomThreshold: { type: Number, attribute: 'bloom-threshold' },
  };

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 400px;
      position: relative;
    }
    :host([fill]) {
      height: 100%;
      min-height: 400px;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
  `;

  constructor() {
    super();
    this.background = '#111';
    this.fogColor = '';
    this.fogNear = 0;
    this.fogFar = 0;
    this.fps = 0;
    this.bloom = false;
    this.bloomStrength = 0.85;
    this.bloomRadius = 0.5;
    this.bloomThreshold = 0.5;
    this._lastRenderTs = 0;
    this.object3D = new THREE.Scene();
    this._activeCamera = null;
    this._defaultCamera = null;
    this._renderer = null;
    this._composer = null;
    this._renderPass = null;
    this._bloomPass = null;
    this._rafId = null;
    this._resizeObserver = null;
    this._frameCallbacks = new Set();
    this._pointerEvents = null;
  }

  // Optional postprocessing: RenderPass → UnrealBloom → OutputPass. clearAlpha=0 keeps the
  // transparent background through the composer (bloom RT also clears alpha 0 in three r184).
  // RenderPass camera is repointed each frame to the active camera in the tick loop.
  _setupComposer() {
    const size = this._renderer.getSize(new THREE.Vector2());
    this._composer = new EffectComposer(this._renderer);
    this._composer.setPixelRatio(window.devicePixelRatio);
    this._renderPass = new RenderPass(this.object3D, this._defaultCamera);
    this._renderPass.clearAlpha = 0;
    this._composer.addPass(this._renderPass);
    this._bloomPass = new UnrealBloomPass(size, this.bloomStrength, this.bloomRadius, this.bloomThreshold);
    this._composer.addPass(this._bloomPass);
    this._composer.addPass(new OutputPass());
  }

  setActiveCamera(cam) {
    this._activeCamera = cam;
  }

  getActiveCamera() {
    return this._activeCamera || this._defaultCamera;
  }

  getRenderer() {
    return this._renderer;
  }

  addFrameCallback(fn) {
    this._frameCallbacks.add(fn);
  }

  removeFrameCallback(fn) {
    this._frameCallbacks.delete(fn);
  }

  firstUpdated() {
    const canvas = this.renderRoot.querySelector('canvas');
    this._renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    this._renderer.setPixelRatio(window.devicePixelRatio);
    this._renderer.setClearColor(0x000000, 0);
    this._renderer.shadowMap.enabled = true;
    this._renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this._renderer.localClippingEnabled = true;
    this._renderer.setTransparentSort(reversePainterSortStable);
    this.object3D.background =
      !this.background || this.background === 'transparent' || this.background === 'none'
        ? null
        : new THREE.Color(this.background);

    this._defaultCamera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
    this._defaultCamera.position.set(3, 3, 5);
    this._defaultCamera.lookAt(0, 0, 0);
    if (this.fogColor && this.fogFar > this.fogNear) {
      this.object3D.fog = new THREE.Fog(
        this.fogColor,
        this.fogNear,
        this.fogFar,
      );
    }

    if (this.bloom) this._setupComposer();

    this._resize();
    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(this);

    this._pointerEvents = forwardHtmlEvents(
      canvas,
      () => this._activeCamera || this._defaultCamera,
      this.object3D,
      { batchEvents: false },
    );

    let lastTime = performance.now();
    const tick = () => {
      this._rafId = requestAnimationFrame(tick);
      const now = performance.now();
      if (this.fps > 0) {
        const minInterval = 1000 / this.fps;
        if (now - this._lastRenderTs < minInterval) return;
      }
      const delta = (now - lastTime) / 1000;
      lastTime = now;
      this._lastRenderTs = now;
      for (const cb of this._frameCallbacks) cb(delta);
      const cam = this._activeCamera || this._defaultCamera;
      if (this._composer) {
        this._renderPass.camera = cam;
        this._composer.render(delta);
      } else {
        this._renderer.render(this.object3D, cam);
      }
    };
    tick();
    this._markReady();
    this.dispatchEvent(new CustomEvent('scene-ready', { bubbles: false }));
  }

  updated(changed) {
    if (changed.has('background') && this.object3D.background) {
      this.object3D.background =
      !this.background || this.background === 'transparent' || this.background === 'none'
        ? null
        : new THREE.Color(this.background);
    }
    if (
      changed.has('fogColor') ||
      changed.has('fogNear') ||
      changed.has('fogFar')
    ) {
      if (this.fogColor && this.fogFar > this.fogNear) {
        this.object3D.fog = new THREE.Fog(
          this.fogColor,
          this.fogNear,
          this.fogFar,
        );
      } else {
        this.object3D.fog = null;
      }
    }
  }

  _resize() {
    if (!this._renderer) return;
    const w = this.clientWidth;
    const h = this.clientHeight;
    this._renderer.setSize(w, h, false);
    this._composer?.setSize(w, h);
    this._bloomPass?.setSize(w, h);
    const cam = this._activeCamera || this._defaultCamera;
    if (cam && cam.isPerspectiveCamera) {
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._pointerEvents?.destroy) this._pointerEvents.destroy();
    else if (typeof this._pointerEvents === 'function') this._pointerEvents();
    this._bloomPass?.dispose?.();
    this._composer?.dispose?.();
    if (this._renderer) this._renderer.dispose();
  }

  render() {
    return html`<canvas></canvas><slot></slot>`;
  }
}

customElements.define('three-scene', ThreeScene);
